'use client'

import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { LoadingSpinner } from '../../primitives/styled/loading-spinner'
import { DragDropContext, Droppable, OnDragEndResponder } from '@hello-pangea/dnd'
import { Table } from '@radix-ui/themes'

import { useTableContext, useTableStore } from '../context/table-context'
import { TableLoader } from '../styled'
import TableRow from './table-row'
import TableSummaryRow from './table-summary-row'
import { reorderRows, sortRows, useActionErrorHandler, useTableLayout } from './table-utils'

type TLoaderProps = {
    colSpan: number
}

const Loader = ({ colSpan }: TLoaderProps) => (
    <TableLoader colSpan={colSpan}>
        <LoadingSpinner $size="4rem" />
    </TableLoader>
)

function TableBody<T>() {
    const t = useTranslations('table')

    const data = useTableStore<T, 'data'>('data')
    const total = useTableStore<T, 'total'>('total')
    const loading = useTableStore<T, 'loading'>('loading')
    const pagination = useTableStore<T, 'pagination'>('pagination')
    const sorter = useTableStore<T, 'sorter'>('sorter')

    const { store, id, serverSide, hidePagination, columns, onReorder } = useTableContext<T>()
    const { colSpan } = useTableLayout<T>()
    const handleError = useActionErrorHandler()

    // server data is already sorted and one page; static data is sorted and paged here
    const orderedRows = serverSide ? data : sortRows(data, sorter, columns)
    const pageOffset = serverSide || hidePagination ? 0 : pagination.page * pagination.pageSize
    const visibleRows =
        serverSide || hidePagination
            ? orderedRows
            : orderedRows.slice(pageOffset, pageOffset + pagination.pageSize)

    const [expandedRows, setExpandedRows] = useState<(string | number)[]>([])
    const handleRowExpansion = (id: string | number) => {
        if (expandedRows.includes(id))
            setExpandedRows(expandedRows.filter(row => row != id))
        else
            setExpandedRows([...expandedRows, id])
    }

    const onDragEnd: OnDragEndResponder = async ({ source, destination }) => {
        if (!destination || source.index === destination.index)
            return

        const reordered = reorderRows(orderedRows, pageOffset + source.index, pageOffset + destination.index)
        // the dragged order wins over a client-side sort, which would move the row right back
        store.update({ data: reordered, ...(!serverSide && { sorter: null }) })

        try {
            await onReorder?.(reordered)
        }
        catch (error) {
            handleError(error)
        }
    }

    if (loading) {
        return (
            <Table.Body>
                <Table.Row>
                    <Loader colSpan={colSpan} />
                </Table.Row>
            </Table.Body>
        )
    }

    if (total == 0) {
        return (
            <Table.Body>
                <Table.Row>
                    <Table.Cell colSpan={colSpan}>{t('no-records')}</Table.Cell>
                </Table.Row>
            </Table.Body>
        )
    }

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppableId">
                {provided => (
                    <Table.Body {...provided.droppableProps} ref={provided.innerRef}>
                        {visibleRows.map((row, index) => (
                            <TableRow
                                key={row[id] as string}
                                row={row}
                                index={index}
                                expandedRows={expandedRows}
                                handleRowExpansion={handleRowExpansion}
                            />
                        ))}
                        {provided.placeholder}
                        <TableSummaryRow />
                    </Table.Body>
                )}
            </Droppable>
        </DragDropContext>
    )
}

export default TableBody
